import React, { useCallback, useEffect, useRef, useState } from 'react';

// ── THE HERO'S PHOTOGRAPHIC BACKDROP ─────────────────────────────────────────
// Five interior shots, cross-fading behind the hero.
//
// NO CONTROLS. There were dots to pick a photo, and they were the only thing
// on the first screen competing with the two buttons for a click. The pictures
// are scenery — nobody arrives wanting to browse them, and a control implies
// there is something here worth choosing between. Without them the set is
// ambient: it changes on its own and asks nothing.
//
// WHY THESE ARE LOCAL FILES. They were uploaded as ~2MB PNGs (1672×941) to
// Firebase Storage — ten megabytes for the first screen of a marketing page,
// on whatever connection the visitor happens to have. Re-encoded to WebP they
// come to 777KB for all five with no visible loss, and served from the same
// origin as the page they skip a second DNS lookup and TLS handshake.
//
// The cost of that choice: replacing a photo now means replacing the file and
// deploying, where re-uploading to Storage would have been enough. Worth it for
// the one screen every visitor sees before they have decided to stay.
const PHOTOS = [
  '/images/hero/photo1.webp',
  '/images/hero/photo2.webp',
  '/images/hero/photo3.webp',
  '/images/hero/photo4.webp',
  '/images/hero/photo5.webp',
];

// ── THE BLUR-UP PLACEHOLDERS ─────────────────────────────────────────────────
// Each photo at 20px wide, ~850 characters. Inline rather than five more
// requests: they exist precisely to cover the moment BEFORE a request can come
// back, so a placeholder that has to be fetched is no placeholder at all.
// Scaled up under a blur they read as the photo's colour and rough composition,
// which is all that is needed to stop the hero flashing empty.
const BLUR = [
  'data:image/webp;base64,UklGRnQCAABXRUJQVlA4WAoAAAAgAAAAEwAACgAASUNDUMgBAAAAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADZWUDgghgAAABAEAJ0BKhQACwA+0VSjS6gkoyGwCAEAGglAE6ZQAGgvtop0atQxUxAAAP7hj8y/gn6BoiBlT7Cm5eE7LaDkAK1SwbTI0aW7hP6WjbiAdzcx/YruM8GBkT1zVOG+Dt2pGkSkvDceoIeF+vXTau9Z9zNxhowHTHFSw7k+JzmDZi2OjleswAAA',
  'data:image/webp;base64,UklGRmgCAABXRUJQVlA4WAoAAAAgAAAAEwAACgAASUNDUMgBAAAAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADZWUDggegAAAJADAJ0BKhQACwA+0VSjS6gkoyGwCAEAGglAE6ABWHNVBbLR+AAA/tyZ757hm1kyi+BQuvsksHn8cy52lsX9iM9wBNNCBk55C33YuG20/+9c3fOxlXlCI3WuMjILJ3pen9WcbWkQIXzN+UDVJozP1dT/3ZJuAEQ10QAA',
  'data:image/webp;base64,UklGRmwCAABXRUJQVlA4WAoAAAAgAAAAEwAACgAASUNDUMgBAAAAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADZWUDggfgAAAFAEAJ0BKhQACwA+0VajS6gkoyGwCAEAGglkALsvAAHhsX+PZ3FnC7Y5LUAA/rpwizQiHNXTH20429PECKSAtwJmsx+5ns+Ffr6C5JICQCW+6D2NsaeSZ8ywnRpbsZtaLD+6Nn+NAaRUkOS5TBC3tihqBhGtuOkKpJrEqiAAAA==',
  'data:image/webp;base64,UklGRlwCAABXRUJQVlA4WAoAAAAgAAAAEwAACgAASUNDUMgBAAAAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADZWUDggbgAAAJADAJ0BKhQACwA+0VajS6gkoyGwCAEAGglAE6ABXiyCcOq02AAA/sXlw+6kWZN6FykoKnjcZgMtX6jGQ7NYs4fS0EkfnQXu7tiZOGdxEdNYBgiWdJzXWGj352E0zRhsWO6JtIlURTsRnsAKtgAA',
  'data:image/webp;base64,UklGRmwCAABXRUJQVlA4WAoAAAAgAAAAEwAACgAASUNDUMgBAAAAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADZWUDggfgAAAFAEAJ0BKhQACwA+0VSjS6gkoyGwCAEAGglAE6ZQjuAFXip3XdonQmCBmoAA/qjVA0htQQJr9kI3z6U/xu916LQLeyZlTQlce3un6Ob8prH3ZEJWXQ2Zm/LLC57c4EaGHywVzNw0jiDFqCNvyb8jUGm0Br2DEELsV/gGN9pYAA==',
];

// Long enough to look at a room, short enough that a visitor who stays a few
// seconds sees the set is a set. A slower pace read as a static image.
const DWELL_MS = 6500;

export default function HeroBackdrop() {
  const [index, setIndex] = useState(0);
  // False until the FIRST photo has arrived. The other four have no src before
  // that: starting five downloads at once makes the visible one land last,
  // which is the opposite of what a hero needs.
  const [armed, setArmed] = useState(false);
  const [ready, setReady] = useState(() => new Set());
  const [paused, setPaused] = useState(false);

  // ── "IT HAS ARRIVED" ───────────────────────────────────────────────────────
  // `onLoad` ALONE IS NOT ENOUGH, and this is the bug that hid the photographs
  // on every visit after the first. A cached image finishes loading before
  // React has attached the handler, so the event has already been and gone by
  // the time anything is listening: the <img> sat at opacity 0 forever and all
  // anyone ever saw was the 20px placeholder blown up to full width — which
  // looks exactly like a photo that failed to load.
  //
  // The ref runs on mount, so it catches the images that were already there;
  // `onLoad` catches the ones still in flight. Returning the SAME Set when the
  // index is already in it is what stops the ref path from re-rendering on
  // every pass and looping.
  const markReady = useCallback((i) => {
    setReady(prev => (prev.has(i) ? prev : new Set(prev).add(i)));
    if (i !== 0) return;
    // AT IDLE, not immediately. The first photo being decoded is the moment the
    // hero becomes usable; spending the next frames fetching four more competes
    // with it for the connection.
    const arm = () => setArmed(true);
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(arm, { timeout: 2500 });
    } else {
      setTimeout(arm, 1200);
    }
  }, []);

  // `complete` is true for a decoded image AND for one that failed, so the
  // dimension check is what separates them — a broken file must not be faded
  // in over the placeholder that is standing in for it.
  const catchCached = useCallback((el, i) => {
    if (el && el.complete && el.naturalWidth > 0) markReady(i);
  }, [markReady]);

  // ── AUTO-ADVANCE ───────────────────────────────────────────────────────────
  // Stopped in three cases, each for its own reason:
  //   · reduced motion — a cross-fade that changes the whole screen is exactly
  //     what that preference is about, so the set becomes manual only;
  //   · a hidden tab — an interval that keeps firing behind another tab is
  //     work nobody is watching;
  //   · pointer over the hero — someone reading the copy should not have the
  //     backdrop change under it.
  const reduced = useRef(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reduced.current = mq.matches;
    const onChange = () => { reduced.current = mq.matches; };
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  useEffect(() => {
    if (paused || reduced.current) return undefined;
    const id = setInterval(() => {
      if (!document.hidden) setIndex(i => (i + 1) % PHOTOS.length);
    }, DWELL_MS);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <div
      className="lnd-bd"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      // Decorative: the photos illustrate the copy, they do not add to it, and
      // a screen reader announcing five interior shots would be noise.
      aria-hidden="true"
    >
      {PHOTOS.map((src, i) => (
        <div
          key={src}
          className={`lnd-bd-slide${i === index ? ' is-on' : ''}`}
          style={{ backgroundImage: `url("${BLUR[i]}")` }}
        >
          <img
            ref={(el) => catchCached(el, i)}
            className={`lnd-bd-img${ready.has(i) ? ' is-ready' : ''}`}
            src={i === 0 || armed ? src : undefined}
            alt=""
            decoding="async"
            // The first is the largest thing on the first screen — the metric
            // every page-speed report leads with. The rest are explicitly
            // deprioritised so they cannot compete with it.
            loading={i === 0 ? 'eager' : 'lazy'}
            fetchpriority={i === 0 ? 'high' : 'low'}
            onLoad={() => markReady(i)}
            draggable={false}
          />
        </div>
      ))}

      {/* THE SCRIM, IN TWO PARTS. A single flat wash dark enough for small text
          also flattens the photograph into a grey rectangle. A vertical
          gradient anchors the top and bottom edges (where the nav and the
          scroll cue sit) while a horizontal one darkens the side the copy is
          on, so the picture stays a picture where nothing is written over it. */}
      <div className="lnd-bd-scrim" />
    </div>
  );
}
